/**
 * Rules that exist for legal reasons, in one place: what TixInfo leaves out, how it names what
 * is protected, what a page must say, whose pictures it may show and who we say we are in an
 * email. They come from a lawyer's answers of 2026-10-04 and his re-check the same day
 * (docs/PLAN.md §12); each rule says why. The build (gen-catalog, gen-longtail, the picture
 * service), the edge, the radar and the site all read this file, so a new answer or a new
 * permission is one edit here.
 *
 * PUBLIC FACTS ONLY: this file is bundled into the browser.
 */

// ---- football ---------------------------------------------------------------------------------

const UK = new Set(['GB', 'UK', 'United Kingdom', 'England', 'Scotland', 'Wales', 'Northern Ireland']);

/** Association football: the partners' "soccer", SeatGeek's "mls".
 * @param {{ category?: string | null, kind?: string | null } | null | undefined} e */
export const isAssociationFootball = (e) => e?.category === 'Sports' && (e.kind === 'soccer' || e.kind === 'mls');

/** Association football played in the United Kingdom (decided by where the match is played,
 *  so no club list goes stale).
 * @param {{ category?: string | null, kind?: string | null, country?: string | null } | null | undefined} e */
export const isUkFootball = (e) => isAssociationFootball(e) && UK.has(String(e?.country ?? ''));

/**
 * UK football is listed. The lawyer first advised leaving it out (section 166 of the Criminal
 * Justice and Public Order Act 1994: advertising tickets for a designated match without the
 * organiser's authority), then rechecked (2026-10-04): TixInfo sells no tickets and has no tie
 * to them, so listing them is neither illegal nor a grey area. Set false to leave every UK
 * match out again — both builds, the edge and search follow `notListed`.
 */
export const UK_FOOTBALL_LISTED = true;

// ---- one switch per place and kind --------------------------------------------------------------------------------
// THE OWNER DECIDES THESE, WITH HIS LAWYER (owner, 2026-10-23: "the legal switches are in my hands, not yours").
// Every rule below is OFF (listed, indexed and promoted): it only NAMES the events a question is about, for the
// owner's review in docs/LEGAL-REVIEW.md. Never set a switch to false without the owner's written go.
// Each rule says for the events it matches: may we LIST them, may Google INDEX their pages, may we PROMOTE them
// (the homepage rows, "just announced", the press, search's first answers). The build (both gen scripts, the
// homepage), the edge and search all read notListed / notIndexed / notPromoted; an answer is one edit to a rule.
// PUBLIC FACTS ONLY (this file is bundled into the browser).
/** @typedef {{ name?: string | null, artist?: string | null, venue?: string | null, tournament?: string | null, category?: string | null, kind?: string | null, country?: string | null }} LegalEvent */
/** @param {LegalEvent | null | undefined} e */
const country = (e) => String(e?.country ?? '').toUpperCase();
const fold = (/** @type {unknown} */ s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
/** Tennis competitions by name, for rows a partner files as Theater or plain "sports" ("ATP Finals", "French Open"). */
export const TENNIS_TITLE = /\b(?:atp finals|nitto atp|wta finals|french open|roland[- ]garros|australian open(?!\s+golf)|internazionali|davis cup|billie jean king cup|laver cup|wimbledon(?!\s+(?:fc|theatre|park))|miami open|madrid open|italian open|monte[- ]carlo masters|indian wells|bnp paribas open|us open tennis)\b/i;
/** @param {LegalEvent | null | undefined} e */
const isTennis = (e) =>
  (e?.category === 'Sports' && /tennis/i.test(String(e.kind ?? ''))) ||
  // by its name, but only for a row filed Theater or plain "sports", and never a football club ("A.F.C. Wimbledon", "Wimbledon vs. Crawley
  // Town"), an orchestra, a village fair, another sport's event
  (!!e &&
    (e.category === 'Theater' || (e.category === 'Sports' && (!e.kind || e.kind === 'sports'))) &&
    !/a\.?f\.?c\.?(?![a-z])|\bfc\b|\bvs?\.?\s|symphony|orchestra|village|\bfair\b|greyhound|bookfest|golf|darts|squash|pickleball|stadium\b/i.test(titleOf(e)) &&
    TENNIS_TITLE.test(titleOf(e)));
/** @param {LegalEvent | null | undefined} e */
const titleOf = (e) => `${e?.name ?? ''} ${e?.artist ?? ''}`;
/** A UEFA competition match (the competition rides in the title or doc.tournament). @param {LegalEvent | null | undefined} e */
const isUefa = (e) => isAssociationFootball(e) && /\buefa\b|champions league|europa league|conference league/i.test(`${titleOf(e)} ${e?.tournament ?? ''}`);

/**
 * Irish VENUES designated under the Sale of Tickets (Cultural, Entertainment, Recreational and Sporting Events)
 * Act 2021: selling OR ADVERTISING a ticket above its original price there is an offence. Source: the
 * Department of Enterprise's "Register for Designation of Venues and Events" (enterprise.gov.ie, Publications;
 * the page is dated 15 September 2026; PDF read 2026-10-22, 35 venues, none with a revocation date). One pattern
 * per register row, in the register's order; the venue's own text is matched without accents.
 *  Thomond Park, Musgrave Park, University Concert Hall (Limerick), Vicar Street, Live At The Marquee (Cork),
 *  Aviva Stadium, 3Olympia Theatre, 3Arena, Tallaght Stadium, Bord Gais Energy Theatre, Gaiety Theatre, Croke Park,
 *  National Concert Hall, The Sportsground (Galway), Semple Stadium, Royal Dublin Society, Royal Hospital Kilmainham,
 *  Fairview Park, St. Anne's Park, Lime Tree Theatre, Black Box Theatre, Town Hall Theatre (Galway), Glor (Clare),
 *  Whelan's, Liberty Hall Theatre, The Workmans Club, Heineken Big Top (Galway International Arts Festival), Marlay Park,
 *  The Helix, Gleneagle INEC Arena (Kerry), Phoenix Park, King John's Castle, Malahide Demesne, The Academy,
 *  The Ambassador Theatre.
 * NOT here, on purpose: venues the register names only for EVENTS (below), and past events (Cork Opera House,
 * Pairc Ui Chaoimh, Iveagh Gardens, Trinity College 2021-2026); Gaelic Grounds, Fitzgerald Stadium and Pearse
 * Stadium are not in the register at all. Check a venue against the register before adding it.
 */
const IE_DESIGNATED_VENUES = [
  'thomond park', 'musgrave park', 'university concert hall', 'vicar street', 'live at the marquee', 'aviva stadium', 'olympia theatre',
  '3 ?arena', 'tallaght stadium', 'bord gais', 'gaiety theatre', 'croke park', 'national concert hall', 'the sportsground', 'semple stadium',
  'royal dublin society', '\\brds\\b', 'royal hospital kilmainham', 'fairview park', "st\\.? anne'?s park", 'lime tree theatre', 'black box theatre',
  'town hall theatre', '\\bglor\\b', "whelan'?s", 'liberty hall theatre', 'workmans club', 'heineken big top', 'marlay park', '\\bthe helix\\b',
  'gleneagle', 'phoenix park', "king john'?s castle", 'malahide (?:demesne|castle)', 'the academy', 'the ambassador',
].map((p) => new RegExp(p));
/** EVENTS the register designates at venues it does not designate as venues, still to come (same source). */
const IE_DESIGNATED_EVENTS = [
  { venue: /slane castle/, name: /oasis|fontaines/ }, // Fontaines D.C. 27-29 Aug 2027; Oasis 26-27 Jun and 4-5 Sep 2027
  { venue: /stradbally/, name: /electric picnic/ }, // Electric Picnic 2027
];
/** @param {LegalEvent | null | undefined} e */
const isIrishDesignated = (e) => {
  if (country(e) !== 'IE') return false;
  const v = fold(e?.venue);
  const t = fold(titleOf(e));
  return IE_DESIGNATED_VENUES.some((r) => r.test(v)) || IE_DESIGNATED_EVENTS.some((d) => d.venue.test(v) && d.name.test(t));
};

/**
 * @typedef {{ id: string, label: string, why: string, test: (e: LegalEvent) => boolean, listed: boolean, indexed: boolean, promoted: boolean }} LegalRule
 * @type {LegalRule[]}
 */
export const LEGAL_RULES = [
  { id: 'wimbledon', label: 'Wimbledon sessions that are not debentures', why: 'AELTC cancels resold tickets; debenture holders may resell, nobody else', listed: true, indexed: true, promoted: true,
    // the tournament, not AFC Wimbledon or the New Wimbledon Theatre: a tennis row, or the AELTC's own grounds
    test: (e) => ((isTennis(e) && /wimbledon/i.test(titleOf(e))) || /all england (?:lawn tennis|club)/i.test(String(e?.venue ?? ''))) && !/debenture/i.test(titleOf(e)) },
  { id: 'tr-passolig', label: 'Turkish football (Passolig-bound)', why: "a Passolig card in the buyer's own name is needed to enter; a resold ticket cannot be used", listed: true, indexed: true, promoted: true,
    // Turkish home games; a neutral cup tie played in a Turkish stadium (the Spanish Super Cup semifinals) is not Passolig-bound
    test: (e) => isAssociationFootball(e) && country(e) === 'TR' && !/super ?cup|supercopa|supercoppa/i.test(titleOf(e)) },
  { id: 'australian-open', label: 'Australian Open', why: "Victoria's Major Events Act caps resale at face value plus 10% for declared events and wants the face value shown", listed: true, indexed: true, promoted: true,
    test: (e) => (isTennis(e) && /australian open/i.test(titleOf(e))) || (isTennis(e) && country(e) === 'AU' && /rod laver|melbourne park|john cain|margaret court/i.test(String(e?.venue ?? ''))) },
  { id: 'ie-designated', label: 'Irish designated venues', why: 'Sale of Tickets Act 2021: selling or ADVERTISING above the original price for designated venues is an offence', listed: true, indexed: true, promoted: true,
    test: (e) => isIrishDesignated(e) },
  { id: 'it-football-tennis', label: 'Italy: football and tennis', why: 'named tickets above 5,000 and AGCOM can order a site blocked', listed: true, indexed: true, promoted: true,
    test: (e) => country(e) === 'IT' && (isAssociationFootball(e) || isTennis(e)) },
  { id: 'fr-football-tennis', label: 'France: football and tennis', why: 'Penal Code art. 313-6-2 punishes facilitating habitual unauthorised resale', listed: true, indexed: true, promoted: true,
    test: (e) => country(e) === 'FR' && (isAssociationFootball(e) || isTennis(e)) },
  { id: 'uefa-finals', label: 'UEFA finals', why: 'named tickets; UEFA cancels resold ones', listed: true, indexed: true, promoted: true,
    test: (e) => isUefa(e) && /\bfinal\b/i.test(titleOf(e)) && !/semi|quarter/i.test(titleOf(e)) },
  { id: 'uefa-clubs', label: 'UEFA club football', why: 'D9: listed, not promoted; clubs void resold tickets', listed: true, indexed: true, promoted: true, test: (e) => isUefa(e) },
  { id: 'uk-football', label: 'UK football', why: 'D9: listed (section 166A exempts a non-UK service), not promoted; clubs void resold tickets', listed: true, indexed: true, promoted: true, test: (e) => isUkFootball(e) },
  { id: 'nl-football', label: 'Dutch football', why: 'D9: listed, not promoted; KNVB tickets are ID- and app-bound', listed: true, indexed: true, promoted: true,
    test: (e) => isAssociationFootball(e) && country(e) === 'NL' },
];

/**
 * Tickets the ORGANISER controls: PDC darts and association football (UEFA, UK, Dutch and every other
 * club or national-team match: clubs and federations void resold tickets, see the rules above). A
 * partner's buyer protection says nothing a buyer can lean on there, so the price board shows no
 * protection line on these pages (W1-2, owner 2026-10-22, until the partners answer in writing, D5).
 * @param {{ name?: string | null, artist?: string | null, tournament?: string | null, category?: string | null, kind?: string | null } | null | undefined} e
 */
export const isOrganiserControlled = (e) =>
  isAssociationFootball(e) || (e?.category === 'Sports' && /\b(?:pdc|darts?)\b/i.test(`${e?.name ?? ''} ${e?.artist ?? ''} ${e?.tournament ?? ''} ${e?.kind ?? ''}`));

/** The rules an event falls under (for logs and tests). @param {LegalEvent | null | undefined} e */
export const legalRulesOf = (e) => (e ? LEGAL_RULES.filter((r) => r.test(e)) : []);

/** An event we do not list for a legal reason (UK_FOOTBALL_LISTED, and the rules above with `listed: false`).
 *  Both builds, the edge and search follow this.
 * @param {LegalEvent | null | undefined} e */
export const notListed = (e) => (!UK_FOOTBALL_LISTED && isUkFootball(e)) || LEGAL_RULES.some((r) => !r.listed && r.test(e ?? {}));

/** A listed event Google must not index (its page carries noindex; the build keeps it out of the sitemaps).
 * @param {LegalEvent | null | undefined} e */
export const notIndexed = (e) => LEGAL_RULES.some((r) => (!r.indexed || !r.listed) && r.test(e ?? {}));

/** A listed event we do not promote: not on the homepage rows, not in "just announced", not in the press.
 *  Anything not indexed or not listed is not promoted either.
 * @param {LegalEvent | null | undefined} e */
export const notPromoted = (e) => LEGAL_RULES.some((r) => (!r.promoted || !r.indexed || !r.listed) && r.test(e ?? {}));

// ---- the Los Angeles 2028 Games ---------------------------------------------------------------
// The lawyer (re-check, 2026-10-04): strict about the WORDING. Every sport its own page, every
// session its own name — in neutral, factual words, never the marks.

/** LA28's own mark, as the partners write it ("LA28 Summer Games: Swimming - SWM03"). */
export const LA28_MARK = /\bLA ?28\b/i;
/** Words 36 U.S.C. §220506 reserves to the US Olympic & Paralympic Committee: never in a name,
 *  heading, URL or meta tag of ours. A listing that needs them and is not an LA28 session is
 *  left out (rare: "WNBF WA State Olympic Natural"). */
export const PROTECTED_WORDS = /olympic|paralympic/i;
/** The neutral, factual name of the Games on our pages (lawyer: "2028 Summer Games in Los
 *  Angeles", no marks in URLs). */
export const LA28_NAME = 'Los Angeles 2028 Summer Games';

/** A partner's listing of an LA28 session: its title or performer carries LA28's mark.
 *  Paralympic sessions are not renamed (no partner lists them yet; "Summer Games" would be wrong).
 * @param {string | null | undefined} title @param {string | null | undefined} performer */
export const isLa28Listing = (title, performer) => {
  const t = `${title ?? ''} ${performer ?? ''}`;
  return LA28_MARK.test(t) && !/paralympic/i.test(t);
};

/** A session's own name, under the neutral name, whatever words the partner used:
 *  "LA28 Summer Games: Swimming - SWM03" → "Los Angeles 2028 Summer Games: Swimming - SWM03".
 * @param {string | null | undefined} title */
export const la28Title = (title) => {
  const rest = String(title ?? '')
    .replace(/\bLA ?28\b/gi, ' ')
    .replace(/\b(?:summer\s+)?olympics?(?:\s+games)?\b/gi, ' ')
    .replace(/\bsummer games\b/gi, ' ')
    .replace(/^[\s:–—-]+/, '')
    .replace(/\s+/g, ' ')
    .trim();
  return rest ? `${LA28_NAME}: ${rest}` : LA28_NAME;
};

/** LA28's session codes ("SWM03", "BK309", "CER01A"). */
const SESSION_CODE = /^[A-Z][A-Z0-9]{2}\d{2}[A-Z]?$/;

/** The sport a session belongs to, which has its own list page — the act of every session page:
 *  "LA28 Summer Games: Swimming - SWM03" → "Los Angeles 2028 Summer Games: Swimming". The
 *  Games' name alone when the title names no sport.
 * @param {string | null | undefined} title */
export const la28Act = (title) => {
  const rest = la28Title(title).slice(LA28_NAME.length).replace(/^[\s:–—-]+/, '');
  const sport = rest
    .split(/\s+-\s+/)[0]
    .replace(/\s*\([A-Z][A-Z0-9]{2}\d{2}[A-Z]?\)\s*$/, '')
    .trim();
  return sport && !SESSION_CODE.test(sport) ? `${LA28_NAME}: ${sport}` : LA28_NAME;
};

/** A name that is the Games' (a session's act, a sport's list page).
 * @param {string | null | undefined} s */
export const isLa28Name = (s) => /los angeles 2028|summer games/i.test(String(s ?? ''));

/** An LA28 session page (by its date and neutral name): it carries the non-affiliation
 *  disclaimer above the fold (src/lib/notices.ts), and its picture is its venue's.
 * @param {{ date?: string | null, name?: string | null, artist?: string | null }} e */
export const isLa28 = (e) => !!e?.date && e.date >= '2028-07-01' && e.date <= '2028-09-30' && isLa28Name(`${e.name ?? ''} ${e.artist ?? ''}`);

// ---- pictures ---------------------------------------------------------------------------------
// The lawyer first said no to both, then read SeatGeek's and Ticketmaster's terms and API rules
// (re-check, 2026-10-04): vaguely worded, but this use is allowed. Club logos may be used too,
// as long as the image itself is not copyrighted (ours are Wikimedia Commons' free files).

/** SeatGeek's photos (its image CDN by performer id, its matchup composites). */
export const SEATGEEK_PHOTOS = true;

/** Ticketmaster's API photos (acts outside sports only: league and team imagery belongs to the
 *  leagues — scripts/pictures/resolve.mjs, the radar). */
export const TICKETMASTER_PHOTOS = true;

/** Whose photo an image URL is: 'seatgeek', 'ticketmaster' or 'other' (ours, Wikimedia's, a partner's).
 * @param {string | null | undefined} url */
export const photoSource = (url) => {
  const u = String(url ?? '');
  if (/(^|[./])seatgeek(images)?\.com\//i.test(u)) return 'seatgeek';
  if (/(^|[./])(ticketm\.net|ticketmaster\.[a-z.]+|livenation\.[a-z.]+)\//i.test(u)) return 'ticketmaster';
  return 'other';
};

/** May a page show this picture? SeatGeek's and Ticketmaster's only while their switch is on.
 * @param {string | null | undefined} url */
export const photoAllowed = (url) => {
  const s = photoSource(url);
  return s === 'seatgeek' ? SEATGEEK_PHOTOS : s === 'ticketmaster' ? TICKETMASTER_PHOTOS : true;
};

// ---- who we are, in every email ---------------------------------------------------------------

/** Price alerts and outreach say who sends them (lawyer, 2026-10-04: business name, KVK number,
 *  business email, a postal address and an opt-out in every message). The postal address lives
 *  in scripts/outreach/config.json (the owner's), not here. */
export const BUSINESS = {
  name: 'Tixinfo.com',
  kvk: '89973216',
  email: 'said@tixinfo.com',
  site: 'https://tixinfo.com',
  privacy: 'https://tixinfo.com/privacy',
};
